import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showToast } from '@/components/Layout/Toast';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import type { UserPortfolioPosition, PortfolioCapitalBasis } from '@einhaus/project314-types';
import { trimDecimals } from '@einhaus/meteoric-helpers';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import { useTradingAccounts } from '@/hooks/useTradingAccounts';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import { useAuth } from '@/context/AuthContext';
import type {
    AllocationDirection,
    CapitalBasisOption,
    ClosingTradeType,
    LinkedTradingAccountRow,
    PortfolioCapitalAllocationDisplayRow,
    PortfolioLimitNotice,
    PortfolioWithAccountingFields,
    PositionTableData,
    TradeConfirmation
} from './components/types';
import { capitalBasisLabels } from './components/constants';
import AllocateFundsModal from './components/AllocateFundsModal';
import CapitalDetailsModal from './components/CapitalDetailsModal';
import ExecutionCapitalBanner from './components/ExecutionCapitalBanner';
import ModifyOrderModal from './components/ModifyOrderModal';
import { modifyOrderPrice, type ModifiableOrder } from './components/modifyOrderPrice';
import PortfolioActionsCard from './components/PortfolioActionsCard';
import PortfolioSummaryCard from './components/PortfolioSummaryCard';
import PerformanceGlanceCard from './components/PerformanceGlanceCard';
import PositionsCard from './components/PositionsCard';
import TradeModal from './components/TradeModal';
import {
    buildPositionsTableData,
    formatCurrency,
    getDefaultAllocationCapitalBasis,
    getDefaultClosingType,
    hasBrokerCapitalAllocationAccount,
    getPortfolioAllocatedAmountForBasis,
    getPortfolioBalanceSummary,
    getPortfolioLimitNotices,
    getPositionFetchTargets,
    getPositionPremiumReceived,
    isOpenShortOptionPosition
} from './components/helpers';

const Portfolio: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const portfolioId = id ? parseInt(id) : 0;
    const queryClient = useQueryClient();
    const { isImpersonating, sessionPolicy } = useAuth();

    const [error, setError] = useState<string | null>(null);
    const [isTradeModalOpen, setIsTradeModalOpen] = useState<boolean>(false);
    const [isAllocateModalOpen, setIsAllocateModalOpen] = useState<boolean>(false);
    const [isCapitalDetailsModalOpen, setIsCapitalDetailsModalOpen] = useState<boolean>(false);
    const [selectedPosition, setSelectedPosition] = useState<UserPortfolioPosition | null>(null);

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    // Numeric form fields hold `null` while empty so a cleared field is not read back as 0.
    const [tradeQuantity, setTradeQuantity] = useState<number | null>(null);
    const [tradeConfirmation, setTradeConfirmation] = useState<TradeConfirmation | null>(null);
    const [isTradeLoading, setIsTradeLoading] = useState<boolean>(false);
    const [tradeError, setTradeError] = useState<string | null>(null);
    const [tradeType, setTradeType] = useState<ClosingTradeType>('sellToClose');
    const [limitPrice, setLimitPrice] = useState<number | null>(null);

    // Modify order state
    const [isModifyOrderModalOpen, setIsModifyOrderModalOpen] = useState<boolean>(false);
    const [orderToModify, setOrderToModify] = useState<ModifiableOrder | null>(null);
    const [modifyPrice, setModifyPrice] = useState<number | null>(null);
    const [isModifyOrderLoading, setIsModifyOrderLoading] = useState<boolean>(false);
    const [modifyOrderError, setModifyOrderError] = useState<string | null>(null);

    // Allocate funds state
    const [allocationAmount, setAllocationAmount] = useState<number | null>(null);
    const [allocationDirection, setAllocationDirection] = useState<AllocationDirection>('deposit');
    const [allocationCapitalBasis, setAllocationCapitalBasis] = useState<PortfolioCapitalBasis>('cash');
    const [isAllocationLoading, setIsAllocationLoading] = useState<boolean>(false);
    const [allocationError, setAllocationError] = useState<string | null>(null);

    // Fetch portfolio data with tRPC
    const portfolioQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolio.queryOptions({
                id: portfolioId,
                shouldFetchTrades: true
            }),
            refetchInterval: 10000, // Refetch every 10 seconds for real-time updates
            enabled: portfolioId > 0
        },
        {
            fallbackErrorMessage: 'Failed to load portfolio data',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    const fetchedPortfolios = portfolioQuery.data?.portfolios;
    const portfolio = useMemo<PortfolioWithAccountingFields | null>(() => fetchedPortfolios?.[0] ?? null, [fetchedPortfolios]);

    // The ordinary table resolves stock/option EntityManagers. Native MES paper
    // positions have their own dated-contract/accounting view below.
    const positions = useMemo(() => (portfolio?.positions ?? []).filter((p) => p.derivativeType !== 'Future'), [portfolio]);
    const nativePositions = useMemo(() => (portfolio?.positions ?? []).filter((p) => p.derivativeType === 'Future'), [portfolio]);
    const pendingTrades = useMemo(() => (portfolio?.trades ?? []).filter((trade) => trade.status === 1).length, [portfolio]);

    const positionFetchTargets = useMemo(() => getPositionFetchTargets(positions), [positions]);

    const { entitiesToFetch, optionsToFetch } = positionFetchTargets;
    const entitiesToFetchCount = entitiesToFetch.length;
    const optionsToFetchCount = optionsToFetch.length;

    // Fetch entities using the batched hook
    const { entities } = useFetchEntities(entitiesToFetch);

    // Fetch options with tRPC
    const optionsQuery = useAppQuery(
        {
            ...trpc.entity.fetchOptions.queryOptions({ options: optionsToFetch }),
            enabled: optionsToFetchCount > 0
        },
        {
            fallbackErrorMessage: 'Failed to load options',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    const positionsTableData = useMemo<PositionTableData[]>(
        () =>
            buildPositionsTableData({
                portfolio,
                positions,
                entities,
                entitiesToFetchCount,
                optionsToFetchCount,
                optionsData: optionsQuery.data
            }),
        [entities, entitiesToFetchCount, optionsQuery.data, optionsToFetchCount, portfolio, positions]
    );

    // Fetch trading requisites (strategies, Trade Logic V2 attachments, etc.)
    const requisitesQuery = useTradingRequisites({
        errorConfig: {
            fallbackErrorMessage: 'Failed to fetch trading requisites',
            setError: (msg) => setError(msg)
        }
    });

    const isLoading = portfolioQuery.isLoading || requisitesQuery.isLoading;

    const tradingAccountsQuery = useTradingAccounts({
        errorConfig: {
            fallbackErrorMessage: 'Failed to fetch trading accounts',
            setError: (msg) => setError(msg)
        }
    });

    // Extract strategies from requisites data
    const strategies = useMemo(() => requisitesQuery.requisites?.tradingStrategies || [], [requisitesQuery.requisites?.tradingStrategies]);

    const linkedTradingAccountId = portfolio?.tradingAccountId;

    const linkedTradingAccount = useMemo(() => {
        if (!linkedTradingAccountId) return null;

        return (
            (tradingAccountsQuery.tradingAccounts.find((account) => account.id === linkedTradingAccountId) as
                | LinkedTradingAccountRow
                | undefined) ?? null
        );
    }, [linkedTradingAccountId, tradingAccountsQuery.tradingAccounts]);

    const usesBrokerCapitalAllocation = hasBrokerCapitalAllocationAccount({
        linkedTradingAccount,
        portfolio
    });

    const availableCapitalBasisOptions = useMemo<CapitalBasisOption[]>(() => {
        if (!usesBrokerCapitalAllocation || !linkedTradingAccount) {
            return [{ value: 'cash', label: capitalBasisLabels.cash }];
        }

        return linkedTradingAccount.capitalBasisSummaries.map((summary) => ({
            value: summary.capitalBasis,
            label: `${capitalBasisLabels[summary.capitalBasis]}${summary.totalCapacity !== null ? ` (${formatCurrency(summary.totalCapacity)})` : ''}${
                summary.isSupported ? '' : ` - Unavailable`
            }`,
            disabled: !summary.isSupported
        }));
    }, [usesBrokerCapitalAllocation, linkedTradingAccount]);

    const defaultCapitalBasis = useMemo<PortfolioCapitalBasis>(
        () => getDefaultAllocationCapitalBasis({ availableCapitalBasisOptions, linkedTradingAccount, portfolio }),
        [availableCapitalBasisOptions, linkedTradingAccount, portfolio]
    );

    const selectedAllocationCapitalBasis = availableCapitalBasisOptions.some(
        (option) => option.value === allocationCapitalBasis && !option.disabled
    )
        ? allocationCapitalBasis
        : defaultCapitalBasis;

    const selectedCapitalBasisSummary = useMemo(() => {
        if (!linkedTradingAccount) return null;

        return (
            linkedTradingAccount.capitalBasisSummaries.find((summary) => summary.capitalBasis === selectedAllocationCapitalBasis) ?? null
        );
    }, [selectedAllocationCapitalBasis, linkedTradingAccount]);

    const selectedCapitalBasisAllocatedAmount = useMemo(() => {
        if (!linkedTradingAccount || !portfolio) return 0;

        return getPortfolioAllocatedAmountForBasis({
            tradingAccount: linkedTradingAccount,
            portfolioId: portfolio.id,
            capitalBasis: selectedAllocationCapitalBasis
        });
    }, [selectedAllocationCapitalBasis, linkedTradingAccount, portfolio]);

    const portfolioCapitalAllocationRows = useMemo<PortfolioCapitalAllocationDisplayRow[]>(() => {
        if (!portfolio) return [];

        if (portfolio.portfolioCapitalAllocationRows?.length) {
            return portfolio.portfolioCapitalAllocationRows;
        }

        if (!usesBrokerCapitalAllocation || !linkedTradingAccount) return [];

        return linkedTradingAccount.capitalBasisSummaries.map((summary) => {
            return {
                ...summary,
                label: capitalBasisLabels[summary.capitalBasis],
                portfolioAllocatedAmount: getPortfolioAllocatedAmountForBasis({
                    tradingAccount: linkedTradingAccount,
                    portfolioId: portfolio.id,
                    capitalBasis: summary.capitalBasis
                })
            };
        });
    }, [usesBrokerCapitalAllocation, linkedTradingAccount, portfolio]);

    const unavailableCapitalBasisRows = useMemo(() => {
        return portfolioCapitalAllocationRows.filter((summary) => !summary.isSupported && summary.supportReason);
    }, [portfolioCapitalAllocationRows]);

    const portfolioLimitNotices = useMemo<PortfolioLimitNotice[]>(() => {
        if (!usesBrokerCapitalAllocation || !linkedTradingAccount) return [];

        return getPortfolioLimitNotices({
            tradingAccount: linkedTradingAccount,
            portfolioId
        });
    }, [usesBrokerCapitalAllocation, linkedTradingAccount, portfolioId]);

    const executionCapitalRow = useMemo(() => {
        if (!portfolioCapitalAllocationRows.length) return null;

        const configuredBasis =
            portfolio?.capitalProfile?.primaryExecutionCapitalBasis ?? portfolio?.capitalProfile?.reportingBaseCapitalBasis;

        if (configuredBasis) {
            const configuredRow = portfolioCapitalAllocationRows.find((summary) => summary.capitalBasis === configuredBasis);
            if (configuredRow && Math.abs(configuredRow.portfolioAllocatedAmount) > 0) return configuredRow;
        }

        const allocatedRows = portfolioCapitalAllocationRows.filter((summary) => Math.abs(summary.portfolioAllocatedAmount) > 0);
        if (!allocatedRows.length) return null;

        return [...allocatedRows].sort(
            (firstSummary, secondSummary) => secondSummary.portfolioAllocatedAmount - firstSummary.portfolioAllocatedAmount
        )[0];
    }, [portfolio?.capitalProfile, portfolioCapitalAllocationRows]);

    const hasCapitalAllocationAlert =
        unavailableCapitalBasisRows.length > 0 || portfolioLimitNotices.some((notice) => notice.tone === 'warning');

    const openAllocationModal = (capitalBasis: PortfolioCapitalBasis = defaultCapitalBasis) => {
        setAllocationCapitalBasis(capitalBasis);
        setIsAllocateModalOpen(true);
    };

    useEffect(() => {
        if (isLoading) {
            showSpinner();
            return;
        }

        hideSpinner();
    }, [isLoading]);

    const handleSellPosition = useCallback(
        (positionId: number) => {
            if (!sessionPolicy.canClosePositions) {
                showToast('This support session does not include permission to close positions.', 'warning');
                return;
            }

            const position = positions.find((p) => p.id === positionId);

            if (position) {
                setSelectedPosition(position);
                setTradeQuantity(position.quantity);
                setLimitPrice(position.currentPrice);
                setTradeConfirmation(null);
                setTradeError(null);

                // Set the appropriate trade type based on position characteristics
                setTradeType(getDefaultClosingType(position));

                setIsTradeModalOpen(true);
            }
        },
        [positions, sessionPolicy.canClosePositions]
    );

    // No need for a separate useEffect to fetch data
    // The portfolioQuery handles initial loading and refetching

    // Setup event listener for modify order links
    useEffect(() => {
        const handleModifyOrderClick = (event: Event) => {
            // Make sure we only handle clicks on modify-order-link elements
            const target = event.target;
            if (!(target instanceof Element)) return;
            const button = target.closest('.modify-order-link');
            if (!button) return;

            event.preventDefault();

            const orderId = Number(button.getAttribute('data-orderid'));
            const order = portfolio?.pendingOrders?.find((pendingOrder) => pendingOrder.id === orderId);
            if (!order || order.positionId === null) return;

            // Set modal state
            setOrderToModify(order);
            setModifyPrice(order.pricePerShare);
            setModifyOrderError(null);

            // Open the modal
            setIsModifyOrderModalOpen(true);
        };

        // Add event listener to capture clicks on dynamically added modify order links
        document.addEventListener('click', handleModifyOrderClick);

        return () => {
            // Clean up
            document.removeEventListener('click', handleModifyOrderClick);
        };
    }, [portfolio?.pendingOrders]);

    const openShortOptionPositions = useMemo(() => positions.filter((position) => isOpenShortOptionPosition(position)), [positions]);
    const hasOpenShortOptionPositions = openShortOptionPositions.length > 0;

    const openPremiumReceived = useMemo(
        () =>
            trimDecimals(
                openShortOptionPositions.reduce((runningTotal, position) => runningTotal + (getPositionPremiumReceived(position) ?? 0), 0)
            ),
        [openShortOptionPositions]
    );

    // Trade confirmation mutation
    const tradeConfirmationMutation = useTrpcMutation(trpc.user.getPositionTradeConfirmation.mutationOptions(), {
        fallbackErrorMessage: 'Failed to get trade confirmation'
    });

    // Execute trade mutation
    const executeTradesMutation = useTrpcMutation(
        trpc.user.executeClosingTrade.mutationOptions({
            onSuccess: () => {
                // Invalidate portfolio query to refresh data
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: portfolioId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Trade executed successfully',
            fallbackErrorMessage: 'Failed to execute trade'
        }
    );

    const handleGetTradeConfirmation = useCallback(async () => {
        if (!selectedPosition || tradeQuantity === null || limitPrice === null) return;

        setIsTradeLoading(true);
        setTradeError(null);

        try {
            // Use the mutation to get trade confirmation
            const result = await tradeConfirmationMutation.mutateAsync({
                positionId: selectedPosition.id,
                type: tradeType,
                quantityToClose: tradeQuantity,
                portfolioId,
                pricePerShare: limitPrice
            });

            if (result.success && result.confirmation) {
                const { confirmation } = result;
                const estimatedFees = (confirmation.estimatedCommission ?? 0) + (confirmation.estimatedFees ?? 0);
                const estimatedValue =
                    confirmation.estimatedCost ??
                    tradeQuantity * (confirmation.limitPrice ?? limitPrice) * selectedPosition.contractMultiplier;

                setTradeConfirmation({
                    positionId: selectedPosition.id,
                    quantity: tradeQuantity,
                    currentPrice: confirmation.limitPrice ?? limitPrice,
                    estimatedValue,
                    commission: estimatedFees,
                    netValue: estimatedValue - estimatedFees
                });
            }
        } catch (error) {
            console.error('Error getting trade confirmation:', error);
            setTradeError(error instanceof Error ? error.message : 'Failed to get trade confirmation');
        } finally {
            setIsTradeLoading(false);
        }
    }, [selectedPosition, tradeQuantity, portfolioId, tradeType, limitPrice, tradeConfirmationMutation]);

    const handleExecuteTrade = async () => {
        if (!selectedPosition || !tradeConfirmation || tradeQuantity === null || limitPrice === null) return;

        setIsTradeLoading(true);

        try {
            // Use the mutation to execute the trade
            await executeTradesMutation.mutateAsync({
                positionId: selectedPosition.id,
                type: tradeType,
                quantityToClose: tradeQuantity,
                pricePerShare: limitPrice,
                portfolioId
            });

            // Reset state on success
            setIsTradeModalOpen(false);
            setTradeConfirmation(null);
            setTradeQuantity(null);
            setSelectedPosition(null);
        } catch (error) {
            console.error('Error executing trade:', error);
            setTradeError(error instanceof Error ? error.message : 'Failed to execute trade');
        } finally {
            setIsTradeLoading(false);
        }
    };

    // Cancel order mutation
    const cancelOrderMutation = useTrpcMutation(
        trpc.user.cancelOrder.mutationOptions({
            onSuccess: () => {
                // Invalidate portfolio query to refresh data
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: portfolioId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Order successfully canceled',
            fallbackErrorMessage: 'Failed to cancel order'
        }
    );

    // Portfolio allocation mutation
    const portfolioAllocationMutation = useTrpcMutation(
        trpc.user.savePortfolioAllocation.mutationOptions({
            onSuccess: () => {
                // Invalidate portfolio query to refresh data
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: portfolioId }).queryKey
                });

                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingAccounts.queryOptions().queryKey
                });
            }
        }),
        {
            fallbackErrorMessage: 'Failed to save allocation'
        }
    );

    const modifyOrderMutation = useTrpcMutation(trpc.user.modifyOrder.mutationOptions(), {
        fallbackErrorMessage: 'Failed to modify order'
    });

    // Modify the price only; retain the selected order's unfilled broker quantity.
    const handleModifyOrder = async () => {
        if (!orderToModify || modifyPrice === null || isModifyOrderLoading) return;

        setIsModifyOrderLoading(true);
        setModifyOrderError(null);

        try {
            await modifyOrderPrice(orderToModify, modifyPrice, modifyOrderMutation.mutateAsync);
            setIsModifyOrderModalOpen(false);
            setOrderToModify(null);
            showToast('Order successfully modified', 'success');

            void queryClient.invalidateQueries({
                queryKey: trpc.user.fetchPortfolio.queryOptions({ id: portfolioId, shouldFetchTrades: true }).queryKey
            });
            void queryClient.invalidateQueries({
                queryKey: trpc.user.fetchTradingAccounts.queryOptions().queryKey
            });
        } catch (error) {
            setModifyOrderError(error instanceof Error ? error.message : 'Failed to modify order');
        } finally {
            setIsModifyOrderLoading(false);
        }
    };

    // Handle cancellation of pending order
    const handleCancelOrder = async () => {
        if (!orderToModify) return;

        setIsModifyOrderLoading(true);
        setModifyOrderError(null);

        try {
            // Use the mutation to cancel the order
            await cancelOrderMutation.mutateAsync({
                id: orderToModify.id
            });

            // Close modal and reset form
            setIsModifyOrderModalOpen(false);
        } catch (error) {
            console.error('Error canceling order:', error);
            setModifyOrderError(error instanceof Error ? error.message : 'Failed to cancel order');
        } finally {
            setIsModifyOrderLoading(false);
        }
    };

    // Handle portfolio fund allocation
    const handleAllocateFunds = async () => {
        if (!portfolio || allocationAmount === null) return;

        const maxWithdrawAmount =
            usesBrokerCapitalAllocation && linkedTradingAccount ? selectedCapitalBasisAllocatedAmount : portfolio.cashBalance;

        if (allocationDirection === 'withdraw' && allocationAmount > maxWithdrawAmount) {
            setAllocationError(
                usesBrokerCapitalAllocation
                    ? 'Withdrawal amount cannot exceed the portfolio allocation for the selected capital basis'
                    : 'Withdrawal amount cannot exceed available cash balance'
            );

            return;
        }

        setIsAllocationLoading(true);
        showSpinner();
        setAllocationError(null);

        try {
            // Use the mutation to allocate funds
            await portfolioAllocationMutation.mutateAsync({
                id: portfolioId,
                amount: allocationAmount,
                type: allocationDirection === 'deposit' ? 'allocate' : 'deallocate',
                capitalBasis: usesBrokerCapitalAllocation ? selectedAllocationCapitalBasis : 'cash'
            });

            // Close modal and reset form
            setIsAllocateModalOpen(false);
            setAllocationAmount(null);
            setAllocationDirection('deposit');
            setAllocationCapitalBasis(defaultCapitalBasis);

            // Show custom success message
            const action = allocationDirection === 'deposit' ? (usesBrokerCapitalAllocation ? 'allocated' : 'deposited') : 'withdrawn';
            const basisSuffix = usesBrokerCapitalAllocation ? ` (${capitalBasisLabels[selectedAllocationCapitalBasis]})` : '';
            const message = `Successfully ${action} $${allocationAmount.toLocaleString('en-US')} to portfolio${basisSuffix}`;
            showToast(message, 'success');
        } catch (error) {
            console.error('Error allocating funds:', error);
            setAllocationError(error instanceof Error ? error.message : 'Failed to allocate funds');
        } finally {
            setIsAllocationLoading(false);
            hideSpinner();
        }
    };

    const balanceSummary = useMemo(() => getPortfolioBalanceSummary(portfolio), [portfolio]);

    // Render loading state
    if (isLoading) {
        return (
            <UserLayout title={portfolio?.name ? `${portfolio.name} - Portfolio - Project 314` : 'Portfolio - Project 314'}>
                <DataLoadingSpinner />
            </UserLayout>
        );
    }

    // Render error state
    if (error) {
        return (
            <UserLayout title={portfolio?.name ? `${portfolio.name} - Portfolio - Error - Project 314` : 'Portfolio - Error - Project 314'}>
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </UserLayout>
        );
    }

    // Render empty state
    if (!portfolio) {
        return (
            <UserLayout title="Portfolio Not Found - Project 314">
                <div className="container mx-auto">
                    <div className="bg-yellow-100 text-yellow-800 border border-yellow-200 p-4 mb-4 rounded-md">Portfolio not found</div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="2200px" title={`${portfolio.name} - Portfolio - Project 314`}>
            {/* Custom styles for the portfolio page */}
            <style>
                {`
                .text-profit-green {
                    color: #29a740 ;
                }
                `}
            </style>
            {/* Container with padding */}
            <div className="container mx-auto px-4 overflow-x-hidden">
                <div className="w-full">
                    <h1 className="text-2xl font-bold text-white mb-4">
                        <Link className="hover:underline" to="/user/portfolios">
                            Portfolio
                        </Link>{' '}
                        &gt; {portfolio?.name}
                    </h1>
                </div>

                {/* Portfolio Summary */}
                <PortfolioSummaryCard
                    balanceSummary={balanceSummary}
                    pendingTrades={pendingTrades}
                    portfolio={portfolio}
                    portfolioId={portfolioId}
                />

                {usesBrokerCapitalAllocation && linkedTradingAccount && (
                    <ExecutionCapitalBanner
                        executionCapitalRow={executionCapitalRow}
                        hasCapitalAllocationAlert={hasCapitalAllocationAlert}
                        linkedTradingAccount={linkedTradingAccount}
                        onManageAllocation={() => openAllocationModal()}
                        onViewDetails={() => setIsCapitalDetailsModalOpen(true)}
                    />
                )}

                {/* Performance summary linking to the full performance page */}
                <PerformanceGlanceCard portfolioId={portfolioId} />

                {/* Positions Table */}
                {nativePositions.length > 0 && (
                    <section className="rounded border border-blue-800 p-5">
                        <h2 className="text-lg font-semibold">Supervised MES paper positions</h2>
                        {nativePositions.map((position) => (
                            <div className="mt-3" key={position.id}>
                                <p>
                                    {position.quantity} contract held · {position.pendingQuantity} pending · unrealized P&amp;L $
                                    {position.currentValue.toFixed(2)}
                                </p>
                                <Link className="text-blue-400 underline" to={`/tools/futuresPaper?positionId=${position.id}`}>
                                    View dated contract, margin, orders and broker-flat reconciliation
                                </Link>
                            </div>
                        ))}
                    </section>
                )}
                <PositionsCard
                    canClosePositions={sessionPolicy.canClosePositions}
                    entities={entities}
                    hasOpenShortOptionPositions={hasOpenShortOptionPositions}
                    portfolio={portfolio}
                    portfolioId={portfolioId}
                    positions={positions}
                    positionsTableData={positionsTableData}
                    strategies={strategies}
                    onSellPosition={handleSellPosition}
                />

                {/* Portfolio Actions */}
                <PortfolioActionsCard portfolioId={portfolioId} onAllocateFunds={() => openAllocationModal()} />

                {/* Modify Order Modal */}
                {isModifyOrderModalOpen && orderToModify && (
                    <ModifyOrderModal
                        contractMultiplier={
                            positions.find((position) => position.id === orderToModify.positionId)?.contractMultiplier ?? null
                        }
                        error={modifyOrderError}
                        isLoading={isModifyOrderLoading}
                        isOpen={isModifyOrderModalOpen}
                        order={orderToModify}
                        price={modifyPrice}
                        onCancelOrder={handleCancelOrder}
                        onClose={() => setIsModifyOrderModalOpen(false)}
                        onModifyOrder={handleModifyOrder}
                        onPriceChange={setModifyPrice}
                    />
                )}

                {/* Trade Modal */}
                {isTradeModalOpen && (
                    <TradeModal
                        error={tradeError}
                        isImpersonating={isImpersonating}
                        isLoading={isTradeLoading}
                        isOpen={isTradeModalOpen}
                        limitPrice={limitPrice}
                        position={selectedPosition}
                        tradeConfirmation={tradeConfirmation}
                        tradeQuantity={tradeQuantity}
                        tradeType={tradeType}
                        trades={portfolio.trades}
                        onBack={() => {
                            setTradeConfirmation(null);
                            setTradeError(null);
                        }}
                        onClose={() => setIsTradeModalOpen(false)}
                        onExecuteTrade={handleExecuteTrade}
                        onGetQuote={handleGetTradeConfirmation}
                        onLimitPriceChange={setLimitPrice}
                        onTradeQuantityChange={setTradeQuantity}
                        onTradeTypeChange={setTradeType}
                    />
                )}

                {/* Capital Details Modal */}
                {isCapitalDetailsModalOpen && usesBrokerCapitalAllocation && linkedTradingAccount && (
                    <CapitalDetailsModal
                        balanceSummary={balanceSummary}
                        openPremiumReceived={hasOpenShortOptionPositions ? openPremiumReceived : null}
                        isOpen={isCapitalDetailsModalOpen}
                        linkedTradingAccount={linkedTradingAccount}
                        portfolioCapitalAllocationRows={portfolioCapitalAllocationRows}
                        portfolioLimitNotices={portfolioLimitNotices}
                        unavailableCapitalBasisRows={unavailableCapitalBasisRows}
                        onClose={() => setIsCapitalDetailsModalOpen(false)}
                        onManageAllocation={() => {
                            setIsCapitalDetailsModalOpen(false);
                            openAllocationModal();
                        }}
                    />
                )}

                {/* Allocate Funds Modal */}
                {isAllocateModalOpen && (
                    <AllocateFundsModal
                        allocationAmount={allocationAmount}
                        allocationDirection={allocationDirection}
                        availableCapitalBasisOptions={availableCapitalBasisOptions}
                        balanceSummary={balanceSummary}
                        error={allocationError}
                        usesBrokerCapitalAllocation={usesBrokerCapitalAllocation}
                        isLoading={isAllocationLoading}
                        isOpen={isAllocateModalOpen}
                        linkedTradingAccount={linkedTradingAccount}
                        portfolio={portfolio}
                        selectedAllocationCapitalBasis={selectedAllocationCapitalBasis}
                        selectedCapitalBasisAllocatedAmount={selectedCapitalBasisAllocatedAmount}
                        selectedCapitalBasisSummary={selectedCapitalBasisSummary}
                        unavailableCapitalBasisRows={unavailableCapitalBasisRows}
                        onAllocateFunds={handleAllocateFunds}
                        onAmountChange={setAllocationAmount}
                        onCapitalBasisChange={setAllocationCapitalBasis}
                        onClose={() => setIsAllocateModalOpen(false)}
                        onDirectionChange={setAllocationDirection}
                    />
                )}
            </div>
        </UserLayout>
    );
};

export default Portfolio;
